import React from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import HeadJsonLd from '../components/HeadJsonLd';
import { useLanguage } from '../contexts/LanguageContext';

type AreaKey = 'family' | 'debtor' | 'administrative';
type Language = 'bg' | 'en';

type LocalizedPage = {
  title: string;
  description: string;
  badge: string;
  intro: string;
  servicesTitle: string;
  services: [string, string][];
  processTitle: string;
  process: [string, string][];
  experienceLabel: string;
  experienceTitle: string;
  experienceText: string;
  consultationTitle: string;
  consultationText: string;
  consultationCta: string;
};

type AreaContent = {
  routes: Record<Language, string>;
  serviceName: Record<Language, string>;
  pages: Record<Language, LocalizedPage>;
};

const areas: Record<AreaKey, AreaContent> = {
  family: {
    routes: { bg: '/semeino-pravo-roditelski-prava', en: '/family-law-parental-rights' },
    serviceName: { bg: 'Семейно право и родителски права', en: 'Family Law and Parental Rights' },
    pages: {
      bg: {
        title: 'Адвокат по семейно право и родителски права',
        description: 'Правна помощ при развод, родителски права, местоживеене на деца, режим на лични отношения, издръжка и защита на семейства пред социалните служби.',
        badge: 'СЕМЕЙНО ПРАВО И РОДИТЕЛСКИ ПРАВА',
        intro: 'Семейните спорове изискват едновременно ясна правна стратегия и внимателен подход към хората, които ще продължат да бъдат семейство и след приключването на делото. Защитаваме родители и деца с фокус върху устойчиво и законосъобразно решение.',
        servicesTitle: 'Правна помощ при семейни казуси',
        services: [
          ['Развод по взаимно съгласие', 'Подготовка на споразумение и документи за прекратяване на брака, включително въпросите за деца, имущество и издръжка.'],
          ['Развод по исков ред', 'Представителство при спор относно прекратяването на брака, вината и последиците от развода.'],
          ['Родителски права и местоживеене', 'Защита при спор кой родител да упражнява родителските права и къде да живее детето.'],
          ['Режим на лични отношения', 'Определяне или изменение на режима за контакти между детето и родителя, при когото то не живее.'],
          ['Издръжка на деца', 'Определяне, изменение и принудително събиране на издръжка според потребностите на детето и възможностите на родителите.'],
          ['Защита пред социалните служби', 'Правна помощ при проверки, оценки на родителския капацитет и мерки за закрила на детето.'],
          ['Връщане на деца в семейството', 'Представителство при оспорване на настаняване извън семейството и други мерки, които ограничават родителските права.'],
          ['Изменение на постановени мерки', 'Нова съдебна защита при съществена промяна на обстоятелствата след решение за родителски права или контакти.'],
        ],
        processTitle: 'Как работим',
        process: [
          ['1. Изясняваме семейната ситуация', 'Разглеждаме историята на отношенията, потребностите на децата, наличните документи и непосредствените рискове.'],
          ['2. Търсим устойчиво решение', 'Преценяваме дали е възможно споразумение и кои въпроси изискват съдебна защита.'],
          ['3. Защитаваме интереса на клиента и детето', 'Подготвяме доказателствата и провеждаме делото с фокус върху приложимо и дългосрочно решение.'],
        ],
        experienceLabel: 'ОПИТ ОТ ПРАКТИКАТА',
        experienceTitle: 'Последователна защита на родители и деца',
        experienceText: 'Кантората има значителен практически опит по дела за родителски права и връщане на деца в семейството. Сред публично представените случаи е защитата на Даниела Колева, при която съдът отменя решение за разделяне на три деца от тяхната майка.',
        consultationTitle: 'Нуждаете се от защита по семеен спор?',
        consultationText: 'Ранната консултация помага да избегнете действия, които могат да усложнят отношенията или да навредят на позицията Ви в бъдещо производство.',
        consultationCta: 'Запазете консултация',
      },
      en: {
        title: 'Family Law and Parental Rights Attorney',
        description: 'Legal assistance with divorce, parental rights, child residence, contact arrangements, child support and representation before social services in Bulgaria.',
        badge: 'FAMILY LAW AND PARENTAL RIGHTS',
        intro: 'Family disputes require both a clear legal strategy and a careful approach to people who will remain a family after the proceedings end. We represent parents and children with a focus on lawful, workable and lasting solutions.',
        servicesTitle: 'Legal assistance with family matters',
        services: [
          ['Divorce by mutual consent', 'Preparation of the agreement and documents required to end the marriage, including arrangements for children, property and support.'],
          ['Contested divorce', 'Representation in disputes concerning termination of the marriage, fault and the legal consequences of divorce.'],
          ['Parental rights and child residence', 'Representation in disputes over the exercise of parental rights and the child’s primary residence.'],
          ['Parent-child contact arrangements', 'Establishing or changing contact arrangements for the parent with whom the child does not primarily live.'],
          ['Child support', 'Determination, modification and enforcement of support based on the child’s needs and each parent’s means.'],
          ['Representation before social services', 'Legal assistance during assessments, parental-capacity reviews and child-protection measures.'],
          ['Returning children to their family', 'Representation when challenging out-of-home placement and other measures restricting parental rights.'],
          ['Modification of existing measures', 'New court proceedings when circumstances materially change after a parental-rights or contact decision.'],
        ],
        processTitle: 'How we work',
        process: [
          ['1. We understand the family situation', 'We review the history, the children’s needs, available documents and any immediate risks.'],
          ['2. We seek a sustainable solution', 'We assess whether an agreement is possible and which issues require court protection.'],
          ['3. We protect the client and the child', 'We prepare the evidence and conduct proceedings with a focus on a workable long-term outcome.'],
        ],
        experienceLabel: 'PRACTICAL EXPERIENCE',
        experienceTitle: 'Consistent representation of parents and children',
        experienceText: 'The firm has substantial practical experience in parental-rights and family-reunification proceedings. Publicly reported work includes the representation of Daniela Koleva, where the court overturned a decision separating three children from their mother.',
        consultationTitle: 'Do you need representation in a family dispute?',
        consultationText: 'An early consultation can help prevent actions that may complicate family relations or weaken your position in future proceedings.',
        consultationCta: 'Schedule a consultation',
      },
    },
  },
  debtor: {
    routes: { bg: '/zashtita-dlazhnitsi-potrebiteli', en: '/debtor-consumer-protection' },
    serviceName: { bg: 'Защита на длъжници и потребители', en: 'Debtor and Consumer Protection' },
    pages: {
      bg: {
        title: 'Защита на длъжници и потребители',
        description: 'Адвокатска защита срещу ЧСИ, банки, колекторски фирми, бързи кредити и неравноправни договорни клаузи във Варна, София и цяла България.',
        badge: 'ЗАЩИТА НА ДЛЪЖНИЦИ И ПОТРЕБИТЕЛИ',
        intro: 'Задължението не лишава длъжника от права. Проверяваме основанието и размера на претенцията, законосъобразността на изпълнителните действия и договорните условия, за да изградим реалистична стратегия за защита.',
        servicesTitle: 'Правна помощ при задължения и потребителски спорове',
        services: [
          ['Защита при изпълнително дело', 'Проверка на изпълнителното производство и обжалване на незаконосъобразни действия на частен или държавен съдебен изпълнител.'],
          ['Запори и възбрани', 'Защита при запор на трудово възнаграждение, банкови сметки, пенсия или възбрана върху недвижим имот.'],
          ['Спорове с банки', 'Анализ на договори за кредит, начислени лихви, такси, предсрочна изискуемост и други банкови претенции.'],
          ['Колекторски фирми', 'Защита срещу неоснователни или недоказани вземания, натиск и неправомерни способи за събиране.'],
          ['Бързи кредити', 'Проверка за прекомерни разходи, неустойки и неравноправни клаузи в договори за потребителски кредит.'],
          ['Погасителна давност', 'Преценка дали вземането или правото на принудително изпълнение е погасено по давност.'],
          ['Заповедно производство', 'Възражения срещу заповеди за изпълнение и защита в последващ исков процес.'],
          ['Преговори и доброволно уреждане', 'Преговори за разсрочване или уреждане на задължението, когато това е в интерес на клиента.'],
        ],
        processTitle: 'Как работим',
        process: [
          ['1. Проверяваме цялото досие', 'Анализираме договора, съдебните книжа, изпълнителното дело, плащанията и начина на изчисляване на претенцията.'],
          ['2. Определяме сроковете и възраженията', 'Установяваме кои действия могат да бъдат оспорени и кои кратки процесуални срокове трябва да бъдат спазени.'],
          ['3. Избираме работеща стратегия', 'Комбинираме съдебна защита, жалби и преговори според доказателствата и финансовата цел на клиента.'],
        ],
        experienceLabel: 'ОПИТ ОТ ПРАКТИКАТА',
        experienceTitle: 'Защита срещу банки, ЧСИ и колекторски фирми',
        experienceText: 'Кантората работи по множество спорове с финансови институции, монополни дружества и лица, събиращи вземания. Всеки случай започва с проверка дали претендираната сума и използваните способи са законосъобразни.',
        consultationTitle: 'Получили сте заповед, покана или запор?',
        consultationText: 'При дългови и изпълнителни производства сроковете често са кратки. Изпратете документите за преглед възможно най-рано.',
        consultationCta: 'Запазете консултация',
      },
      en: {
        title: 'Debtor and Consumer Protection',
        description: 'Legal protection against private bailiffs, banks, collection agencies, payday lenders and unfair contract terms in Varna, Sofia and throughout Bulgaria.',
        badge: 'DEBTOR AND CONSUMER PROTECTION',
        intro: 'A debt does not deprive a debtor of legal rights. We examine the basis and amount of the claim, the lawfulness of enforcement measures and the contractual terms in order to build a realistic defense strategy.',
        servicesTitle: 'Legal assistance with debt and consumer disputes',
        services: [
          ['Enforcement proceedings', 'Review of enforcement files and appeals against unlawful actions by private or state enforcement agents.'],
          ['Account attachments and property seizures', 'Protection involving salary, bank-account or pension attachments and restrictions registered against real estate.'],
          ['Bank disputes', 'Analysis of credit agreements, interest, fees, acceleration and other claims made by banks.'],
          ['Collection agencies', 'Protection against unsupported claims, pressure and unlawful collection practices.'],
          ['Payday and consumer loans', 'Review of excessive costs, penalties and unfair terms in consumer-credit agreements.'],
          ['Limitation periods', 'Assessment of whether the claim or the right to enforce it has become time-barred.'],
          ['Payment-order proceedings', 'Objections to payment orders and representation in subsequent civil proceedings.'],
          ['Negotiation and settlement', 'Negotiation of installments or settlement where that is in the client’s best interest.'],
        ],
        processTitle: 'How we work',
        process: [
          ['1. We review the complete file', 'We analyze the agreement, court documents, enforcement file, payments and calculation of the claimed amount.'],
          ['2. We identify deadlines and defenses', 'We determine which actions may be challenged and which short procedural deadlines apply.'],
          ['3. We choose a workable strategy', 'We combine court proceedings, appeals and negotiation according to the evidence and the client’s financial objective.'],
        ],
        experienceLabel: 'PRACTICAL EXPERIENCE',
        experienceTitle: 'Protection against banks, enforcement agents and collection agencies',
        experienceText: 'The firm handles numerous disputes involving financial institutions, utility monopolies and debt collectors. Each matter begins with a review of whether the claimed amount and the methods used to collect it are lawful.',
        consultationTitle: 'Have you received a payment order, notice or attachment?',
        consultationText: 'Deadlines in debt and enforcement proceedings are often short. Send the documents for review as early as possible.',
        consultationCta: 'Schedule a consultation',
      },
    },
  },
  administrative: {
    routes: { bg: '/administrativno-pravo', en: '/administrative-law' },
    serviceName: { bg: 'Административно право', en: 'Administrative Law' },
    pages: {
      bg: {
        title: 'Адвокат по административно право',
        description: 'Правна защита срещу административни актове, държавни институции, общини, регулаторни органи и откази за достъп до обществена информация.',
        badge: 'АДМИНИСТРАТИВНО ПРАВО',
        intro: 'Действията на администрацията подлежат на контрол. Представляваме граждани и организации при оспорване на административни актове, откази, санкции и бездействие на държавни и общински органи.',
        servicesTitle: 'Правна помощ при административни спорове',
        services: [
          ['Обжалване на административни актове', 'Проверка и съдебно оспорване на индивидуални и общи административни актове.'],
          ['Спорове с общини', 'Защита при актове, откази и действия на общински администрации и местни органи.'],
          ['Достъп до обществена информация', 'Заявления и съдебно обжалване на откази за предоставяне на обществена информация.'],
          ['Административни санкции', 'Защита при наказателни постановления и други санкционни производства.'],
          ['Регулаторни производства', 'Представителство пред административни и регулаторни органи по въпроси, засягащи права и законни интереси.'],
          ['Спорове с комунални дружества', 'Правна помощ при административни и регулаторни аспекти на спорове с енергийни и ВиК дружества.'],
          ['Бездействие и мълчалив отказ', 'Защита когато администрацията не се произнася в срок или не изпълнява свое законово задължение.'],
          ['Обезщетения от държавата и общините', 'Преценка и предявяване на претенции за вреди от незаконосъобразни актове, действия или бездействия.'],
        ],
        processTitle: 'Как работим',
        process: [
          ['1. Проверяваме акта и преписката', 'Анализираме компетентността на органа, процедурата, мотивите, доказателствата и начина на връчване.'],
          ['2. Следим кратките срокове', 'Определяме точния срок и компетентния орган или съд, пред който защитата трябва да бъде предприета.'],
          ['3. Изграждаме съдебната защита', 'Формулираме нарушенията и исканията и представяме доказателствата, необходими за контрол върху администрацията.'],
        ],
        experienceLabel: 'ОПИТ ОТ ПРАКТИКАТА',
        experienceTitle: 'Прозрачност и контрол върху институциите',
        experienceText: 'Сред публично представените дела на кантората е успешно оспорване на отказ за достъп до информация относно договори за златни концесии. Казусът показва ролята на съдебния контрол за прозрачността на държавното управление.',
        consultationTitle: 'Получили сте административен акт или отказ?',
        consultationText: 'Сроковете за обжалване обикновено са кратки и започват от съобщаването на акта. Потърсете правна помощ преди срокът да изтече.',
        consultationCta: 'Запазете консултация',
      },
      en: {
        title: 'Administrative Law Attorney',
        description: 'Legal protection against administrative acts, state institutions, municipalities, regulatory authorities and refusals of access to public information in Bulgaria.',
        badge: 'ADMINISTRATIVE LAW',
        intro: 'Administrative action is subject to legal review. We represent individuals and organizations challenging administrative decisions, refusals, sanctions and inaction by state and municipal authorities.',
        servicesTitle: 'Legal assistance with administrative disputes',
        services: [
          ['Challenges to administrative acts', 'Review and judicial challenge of individual and general administrative acts.'],
          ['Disputes with municipalities', 'Protection involving decisions, refusals and actions by municipal administrations and local authorities.'],
          ['Access to public information', 'Applications and court appeals against refusals to provide public information.'],
          ['Administrative sanctions', 'Representation involving penalty decrees and other administrative-sanction proceedings.'],
          ['Regulatory proceedings', 'Representation before administrative and regulatory authorities in matters affecting rights and legitimate interests.'],
          ['Utility disputes', 'Legal assistance with administrative and regulatory aspects of disputes involving energy and water providers.'],
          ['Administrative inaction and implied refusals', 'Protection when an authority fails to decide within the statutory period or perform a legal duty.'],
          ['Compensation from the state and municipalities', 'Assessment and pursuit of claims for damage caused by unlawful acts, actions or omissions.'],
        ],
        processTitle: 'How we work',
        process: [
          ['1. We review the act and administrative file', 'We examine the authority’s competence, procedure, reasons, evidence and service of the act.'],
          ['2. We track short appeal periods', 'We determine the exact deadline and the authority or court before which protection must be sought.'],
          ['3. We build the court challenge', 'We identify the violations, formulate the relief sought and present the evidence needed for judicial review.'],
        ],
        experienceLabel: 'PRACTICAL EXPERIENCE',
        experienceTitle: 'Transparency and institutional accountability',
        experienceText: 'The firm’s publicly reported work includes a successful challenge to a refusal of access to information concerning gold-concession agreements. The matter demonstrates the role of judicial review in government transparency.',
        consultationTitle: 'Have you received an administrative decision or refusal?',
        consultationText: 'Appeal periods are usually short and begin when the act is served. Seek legal advice before the deadline expires.',
        consultationCta: 'Schedule a consultation',
      },
    },
  },
};

export default function PracticeAreaPage({ area }: { area: AreaKey }) {
  const { language } = useLanguage();
  const areaContent = areas[area];
  const page = areaContent.pages[language];
  const canonical = `https://advokatnikolov.com${areaContent.routes[language]}`;

  React.useEffect(() => {
    document.body.setAttribute('data-lang', language);
    document.documentElement.setAttribute('lang', language);
    window.scrollTo(0, 0);
  }, [language]);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LegalService',
    name: areaContent.serviceName[language],
    description: page.description,
    provider: {
      '@type': 'LegalService',
      name: language === 'bg' ? 'Адвокатска кантора Петър Николов' : 'Petar Nikolov Law Firm',
      url: 'https://advokatnikolov.com',
      telephone: '+359882744951',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'бул. Цар Освободител 44, ет. 4, офис 6',
        addressLocality: 'Варна',
        addressCountry: 'BG',
      },
    },
    areaServed: ['Varna', 'Sofia', 'Bulgaria'],
    serviceType: page.services.map(([title]) => title),
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${page.title} | ${language === 'bg' ? 'Адвокат Петър Николов' : 'Attorney Petar Nikolov'}`}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="bg" href={`https://advokatnikolov.com${areaContent.routes.bg}`} />
        <link rel="alternate" hrefLang="en" href={`https://advokatnikolov.com${areaContent.routes.en}`} />
      </Helmet>
      <HeadJsonLd id="practice-area-jsonld" data={structuredData} />
      <NavBar />

      <main>
        <section className="pt-32 pb-20 bg-gradient-to-br from-navy via-navy/95 to-accent text-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
            <span className="inline-flex rounded-full bg-gold/15 border border-gold/30 px-4 py-2 text-sm font-semibold tracking-wide text-gold mb-6">{page.badge}</span>
            <h1 className="text-4xl md:text-5xl font-serif mb-6">{page.title}</h1>
            <p className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed text-gray-200">{page.intro}</p>
            <a href="/#contact" className="btn-primary inline-flex mt-10">{page.consultationCta}</a>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="section-title text-center mb-12">{page.servicesTitle}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {page.services.map(([title, description]) => (
                <article key={title} className="rounded-2xl border border-gray-100 bg-white p-7 shadow-lg">
                  <div className="flex items-start gap-4">
                    <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-gold" />
                    <div>
                      <h3 className="text-xl font-serif text-navy mb-2">{title}</h3>
                      <p className="text-slate leading-relaxed">{description}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="section-title text-center mb-12">{page.processTitle}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {page.process.map(([title, description]) => (
                <article key={title} className="rounded-2xl bg-white p-7 shadow-md">
                  <h3 className="text-xl font-serif text-navy mb-3">{title}</h3>
                  <p className="text-slate leading-relaxed">{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <article className="rounded-2xl bg-navy p-8 md:p-10 text-white shadow-xl">
              <span className="text-sm font-semibold tracking-wide text-gold">{page.experienceLabel}</span>
              <h2 className="text-3xl font-serif mt-3 mb-5">{page.experienceTitle}</h2>
              <p className="text-gray-200 leading-relaxed">{page.experienceText}</p>
            </article>
          </div>
        </section>

        <section className="py-16 lg:py-20 bg-gradient-to-br from-gold/20 to-gold/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-serif text-navy mb-5">{page.consultationTitle}</h2>
            <p className="text-lg text-slate leading-relaxed mb-8">{page.consultationText}</p>
            <a href="/#contact" className="btn-primary inline-flex">{page.consultationCta}</a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
